// src/pages/personal/analytics/CustomerSection/components/CustomerFilters.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Check, Loader, ChevronDown } from 'lucide-react';
import { Flex } from '@/components/ui/Layout';
import { COMPONENT_STYLES } from '@/theme';
import styles from '../CustomerSection.module.css';

interface CustomerFiltersProps {
  statuses: string[];
  leadSources: string[];
  onChange: (statuses: string[], leadSources: string[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  onReset?: () => void;
}

interface FilterOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

/**
 * Компонент фильтров для раздела клиентов
 * Адаптивный: на десктопе - выпадающее меню, на мобильных - раскрывающаяся карточка
 */
const CustomerFilters: React.FC<CustomerFiltersProps> = ({
  statuses = [],
  leadSources = [],
  onChange,
  disabled = false,
  isLoading = false,
  onReset
}) => {
  const { t } = useTranslation(['customer', 'common']);
  const DEBUG = process.env.NODE_ENV === 'development';
  
  // Состояния компонента
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(statuses);
  const [selectedLeadSources, setSelectedLeadSources] = useState<string[]>(leadSources);
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isLeadSourceFilterOpen, setIsLeadSourceFilterOpen] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  
  // Определяем, это мобильное устройство или нет
  const [isMobile, setIsMobile] = useState(false);
  
  // Рефы для отслеживания кликов вне компонентов
  const statusFilterRef = useRef<HTMLDivElement>(null);
  const leadSourceFilterRef = useRef<HTMLDivElement>(null);
  
  // Проверка размера экрана при монтировании и при изменении размера
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Проверяем размер экрана при монтировании
    checkScreenSize();
    
    // Добавляем слушатель изменения размера окна
    window.addEventListener('resize', checkScreenSize);
    
    // Удаляем слушатель при размонтировании
    return () => {
      window.removeEventListener('resize', checkScreenSize);
    };
  }, []);
  
  // Обновляем локальные значения при изменении пропсов
  useEffect(() => {
    if (!isApplying) {
      setSelectedStatuses(statuses);
      setSelectedLeadSources(leadSources);
    }
  }, [statuses, leadSources, isApplying]);
  
  // Обработчик кликов вне компонентов для закрытия фильтров (только для десктопа)
  useEffect(() => {
    if (isMobile) return; // Пропускаем для мобильной версии
    
    const handleClickOutside = (event: MouseEvent) => {
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target as Node) && isStatusFilterOpen) {
        setIsStatusFilterOpen(false);
      }
      
      if (leadSourceFilterRef.current && !leadSourceFilterRef.current.contains(event.target as Node) && isLeadSourceFilterOpen) {
        setIsLeadSourceFilterOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isStatusFilterOpen, isLeadSourceFilterOpen, isMobile]);
  
  // Доступные статусы клиентов
  const availableStatuses: FilterOption[] = [
    { value: 'new', label: t('statuses.new', 'Новые'), icon: <span>🆕</span> },
    { value: 'rare', label: t('statuses.rare', 'Редкие'), icon: <span>🔍</span> },
    { value: 'regular', label: t('statuses.regular', 'Постоянные'), icon: <span>🔄</span> },
    { value: 'loyal', label: t('statuses.loyal', 'Лояльные'), icon: <span>⭐</span> },
    { value: 'lost', label: t('statuses.lost', 'Потерянные'), icon: <span>👋</span> }
  ];
  
  // Доступные источники лидов
  const availableLeadSources: FilterOption[] = [
    { value: 'instagram', label: 'Instagram', icon: <span>📸</span> },
    { value: 'facebook', label: 'Facebook', icon: <span>👍</span> },
    { value: 'seo', label: 'SEO', icon: <span>🔍</span> },
    { value: 'maps', label: t('sources.maps', 'Карты'), icon: <span>📍</span> },
    { value: 'walk_in', label: t('sources.walk_in', 'Визит в магазин'), icon: <span>🏪</span> },
    { value: 'referral', label: t('sources.referral', 'Реферал'), icon: <span>👥</span> },
    { value: 'other', label: t('sources.other', 'Другое'), icon: <span>📋</span> }
  ];
  
  // Обработчики
  const toggleStatus = (statusValue: string) => {
    setSelectedStatuses(prev => 
      prev.includes(statusValue) 
        ? prev.filter(s => s !== statusValue) 
        : [...prev, statusValue]
    );
  };
  
  const toggleLeadSource = (sourceValue: string) => {
    setSelectedLeadSources(prev => 
      prev.includes(sourceValue) 
        ? prev.filter(s => s !== sourceValue) 
        : [...prev, sourceValue]
    );
  };
  
  const applyFilters = (type: 'status' | 'leadSource') => {
    if (DEBUG) {
      console.log('CustomerFilters - Применение фильтров:', 
        type === 'status' ? selectedStatuses : selectedLeadSources);
    }
    
    setIsApplying(true);
    
    try {
      if (type === 'status') {
        onChange(selectedStatuses, leadSources);
        // Закрываем фильтр после применения только на десктопе
        if (!isMobile) {
          setIsStatusFilterOpen(false);
        }
      } else {
        onChange(statuses, selectedLeadSources);
        // Закрываем фильтр после применения только на десктопе
        if (!isMobile) {
          setIsLeadSourceFilterOpen(false);
        }
      }
    } finally {
      // Добавляем задержку для визуального эффекта
      setTimeout(() => {
        setIsApplying(false);
      }, 300);
    }
  };
  
  const resetFilters = (type: 'status' | 'leadSource' | 'all') => {
    if (DEBUG) console.log('CustomerFilters - Сброс фильтров:', type);
    
    if (type === 'status' || type === 'all') {
      setSelectedStatuses([]);
    }
    
    if (type === 'leadSource' || type === 'all') {
      setSelectedLeadSources([]);
    }
    
    if (type === 'all' && onReset) {
      onReset();
    } else {
      onChange(
        type === 'status' || type === 'all' ? [] : statuses,
        type === 'leadSource' || type === 'all' ? [] : leadSources
      );
      
      // Закрываем фильтр после сброса только на десктопе
      if (!isMobile) {
        if (type === 'status') {
          setIsStatusFilterOpen(false);
        } else if (type === 'leadSource') {
          setIsLeadSourceFilterOpen(false);
        }
      }
    }
  };
  
  // Переключение состояния фильтров
  const toggleStatusFilter = () => {
    if (!isStatusFilterOpen && isLeadSourceFilterOpen) {
      setIsLeadSourceFilterOpen(false);
    }
    setIsStatusFilterOpen(prev => !prev);
  };
  
  const toggleLeadSourceFilter = () => {
    if (!isLeadSourceFilterOpen && isStatusFilterOpen) {
      setIsStatusFilterOpen(false);
    }
    setIsLeadSourceFilterOpen(prev => !prev);
  };
  
  // Определяем, есть ли изменения в фильтрах
  const hasStatusChanges = JSON.stringify(selectedStatuses) !== JSON.stringify(statuses);
  const hasLeadSourceChanges = JSON.stringify(selectedLeadSources) !== JSON.stringify(leadSources);
  
  // Проверка активных фильтров
  const hasActiveStatusFilters = selectedStatuses.length > 0;
  const hasActiveLeadSourceFilters = selectedLeadSources.length > 0;
  const hasAnyActiveFilters = hasActiveStatusFilters || hasActiveLeadSourceFilters;
  
  // Состояние блокировки UI
  const isBlocked = disabled || isLoading || isApplying;
  
  // Стили для кнопок
  const buttonBaseClass = `${COMPONENT_STYLES?.button?.base || 'rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'} text-sm px-4 py-2`;

  // Кнопка-переключатель для фильтра статусов
  const statusFilterButton = (
    <button
      className={`bg-white border border-gray-300 flex items-center gap-2 px-3 py-2 rounded-md ${
        isBlocked ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'
      } ${isStatusFilterOpen ? 'bg-gray-50' : ''} ${
        hasActiveStatusFilters ? styles.filterButtonActive : ''
      }`}
      onClick={toggleStatusFilter}
      disabled={isBlocked}
      type="button"
    >
      <Filter size={16} className={styles.filterIcon} />
      <span>{t('filters.status', 'Статусы')}</span>
      
      {/* Индикатор выбранных фильтров */}
      {hasActiveStatusFilters && (
        <span className={`bg-[#245D33] text-white rounded-full text-xs px-2 py-0.5 min-w-[20px] text-center ${styles.filterBadge}`}>
          {selectedStatuses.length}
        </span>
      )}
      
      <ChevronDown 
        size={16} 
        className={`ml-1 transition-transform ${isStatusFilterOpen ? 'transform rotate-180' : ''}`} 
      />
    </button>
  );

  // Кнопка-переключатель для фильтра источников
  const leadSourceFilterButton = (
    <button
      className={`bg-white border border-gray-300 flex items-center gap-2 px-3 py-2 rounded-md ${
        isBlocked ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'
      } ${isLeadSourceFilterOpen ? 'bg-gray-50' : ''} ${
        hasActiveLeadSourceFilters ? styles.filterButtonActive : ''
      }`}
      onClick={toggleLeadSourceFilter}
      disabled={isBlocked}
      type="button"
    >
      <Filter size={16} className={styles.filterIcon} />
      <span>{t('filters.lead_source', 'Источники')}</span>
      
      {/* Индикатор выбранных фильтров */}
      {hasActiveLeadSourceFilters && (
        <span className={`bg-[#245D33] text-white rounded-full text-xs px-2 py-0.5 min-w-[20px] text-center ${styles.filterBadge}`}>
          {selectedLeadSources.length}
        </span>
      )}
      
      <ChevronDown 
        size={16} 
        className={`ml-1 transition-transform ${isLeadSourceFilterOpen ? 'transform rotate-180' : ''}`} 
      />
    </button>
  );

  // Рендер содержимого фильтра статусов
  const renderStatusFilterContent = () => (
    <>
      <div className="mb-2 font-medium">
        {t('filters.select_statuses', 'Выберите статусы')}
      </div>
      
      {/* Горизонтальный список опций */}
      <div className="flex flex-wrap gap-2 mb-4">
        {availableStatuses.map(status => (
          <div 
            key={status.value} 
            className={`flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors border
            ${selectedStatuses.includes(status.value) 
              ? 'bg-[#245D33] text-white border-[#245D33]' 
              : 'bg-white border-gray-300 hover:bg-gray-50'
            } ${isBlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={() => !isBlocked && toggleStatus(status.value)}
          >
            <div className="flex items-center">
              {status.icon && <span className="mr-2">{status.icon}</span>}
              <span>{status.label}</span>
            </div>
            {selectedStatuses.includes(status.value) && (
              <Check size={16} className="ml-2" />
            )}
          </div>
        ))}
      </div>
      
      {/* Кнопки действий */}
      <Flex justify="between" gap="sm" className="mt-4">
        {/* Кнопка сброса */}
        {selectedStatuses.length > 0 && (
          <button
            type="button"
            onClick={() => resetFilters('status')}
            className={`${buttonBaseClass} ${COMPONENT_STYLES?.button?.secondary || 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
            disabled={isBlocked}
          >
            {t('filters.clear', 'Сбросить')}
          </button>
        )}
        
        {/* Кнопка применения */}
        <button
          type="button"
          onClick={() => applyFilters('status')}
          className={`${buttonBaseClass} ${COMPONENT_STYLES?.button?.primary || 'bg-[#245D33] text-white hover:bg-[#1a4525]'} ${isApplying ? 'animate-pulse' : ''} ml-auto`}
          disabled={isBlocked || (!hasStatusChanges && !isApplying)}
        >
          {isLoading || isApplying ? (
            <Flex align="center" gap="xs">
              <Loader className="animate-spin h-4 w-4" />
              {t('common:loading', 'Загрузка...')}
            </Flex>
          ) : (
            t('filters.apply', 'Применить')
          )}
        </button>
      </Flex>
    </>
  );

  // Рендер содержимого фильтра источников
  const renderLeadSourceFilterContent = () => (
    <>
      <div className="mb-2 font-medium">
        {t('filters.select_lead_sources', 'Выберите источники')}
      </div>
      
      {/* Горизонтальный список опций */}
      <div className="flex flex-wrap gap-2 mb-4">
        {availableLeadSources.map(source => (
          <div 
            key={source.value} 
            className={`flex items-center px-3 py-2 rounded-md cursor-pointer transition-colors border
            ${selectedLeadSources.includes(source.value) 
              ? 'bg-[#245D33] text-white border-[#245D33]' 
              : 'bg-white border-gray-300 hover:bg-gray-50'
            } ${isBlocked ? 'opacity-60 cursor-not-allowed' : ''}`}
            onClick={() => !isBlocked && toggleLeadSource(source.value)}
          >
            <div className="flex items-center">
              {source.icon && <span className="mr-2">{source.icon}</span>}
              <span>{source.label}</span>
            </div>
            {selectedLeadSources.includes(source.value) && (
              <Check size={16} className="ml-2" />
            )}
          </div>
        ))}
      </div>
      
      {/* Кнопки действий */}
      <Flex justify="between" gap="sm" className="mt-4">
        {/* Кнопка сброса */}
        {selectedLeadSources.length > 0 && (
          <button
            type="button"
            onClick={() => resetFilters('leadSource')}
            className={`${buttonBaseClass} ${COMPONENT_STYLES?.button?.secondary || 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
            disabled={isBlocked}
          >
            {t('filters.clear', 'Сбросить')}
          </button>
        )}
        
        {/* Кнопка применения */}
        <button
          type="button"
          onClick={() => applyFilters('leadSource')}
          className={`${buttonBaseClass} ${COMPONENT_STYLES?.button?.primary || 'bg-[#245D33] text-white hover:bg-[#1a4525]'} ${isApplying ? 'animate-pulse' : ''} ml-auto`}
          disabled={isBlocked || (!hasLeadSourceChanges && !isApplying)}
        >
          {isLoading || isApplying ? (
            <Flex align="center" gap="xs">
              <Loader className="animate-spin h-4 w-4" />
              {t('common:loading', 'Загрузка...')}
            </Flex>
          ) : (
            t('filters.apply', 'Применить')
          )}
        </button>
      </Flex>
    </>
  );

  // Мобильная версия компонента
  if (isMobile) {
    return (
      <div className={styles.filterContainer}>
        {/* Контейнер для кнопок фильтров рядом друг с другом */}
        <div className="flex gap-2 mb-2">
          {/* Статус фильтр - кнопка */}
          <div className="flex-1">
            {statusFilterButton}
          </div>
          
          {/* Источник фильтр - кнопка */}
          <div className="flex-1">
            {leadSourceFilterButton}
          </div>
        </div>
        
        {/* Раскрывающаяся карточка для статусов */}
        {isStatusFilterOpen && (
          <div className="mt-2 mb-4 bg-white border border-gray-200 rounded-md shadow-sm p-4">
            {renderStatusFilterContent()}
          </div>
        )}
        
        {/* Раскрывающаяся карточка для источников */}
        {isLeadSourceFilterOpen && (
          <div className="mt-2 mb-4 bg-white border border-gray-200 rounded-md shadow-sm p-4">
            {renderLeadSourceFilterContent()}
          </div>
        )}
        
        {/* Кнопка сброса всех фильтров */}
        {hasAnyActiveFilters && (
          <button
            type="button"
            onClick={() => resetFilters('all')}
            className="px-3 py-2 text-sm text-gray-600 hover:text-[#245D33]"
            disabled={isBlocked}
          >
            {t('filters.reset_all', 'Сбросить все')}
          </button>
        )}
      </div>
    );
  }
  
  // Десктопная версия компонента
  return (
    <div className={styles.filterContainer}>
      {/* Фильтр по статусам - десктопный вариант */}
      <div className="relative" ref={statusFilterRef}>
        {statusFilterButton}
        
        {/* Выпадающая панель фильтра статусов */}
        {isStatusFilterOpen && (
          <div className={`absolute left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-4 min-w-[300px] ${styles.filterDropdown}`}>
            {renderStatusFilterContent()}
          </div>
        )}
      </div>
      
      {/* Фильтр по источникам - десктопный вариант */}
      <div className="relative" ref={leadSourceFilterRef}>
        {leadSourceFilterButton}
        
        {/* Выпадающая панель фильтра источников */}
        {isLeadSourceFilterOpen && (
          <div className={`absolute left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10 p-4 min-w-[300px] ${styles.filterDropdown}`}>
            {renderLeadSourceFilterContent()}
          </div>
        )}
      </div>
      
      {/* Кнопка сброса всех фильтров */}
      {hasAnyActiveFilters && (
        <button
          type="button"
          onClick={() => resetFilters('all')}
          className="px-3 py-2 text-sm text-gray-600 hover:text-[#245D33]"
          disabled={isBlocked}
        >
          {t('filters.reset_all', 'Сбросить все')}
        </button>
      )}
    </div>
  );
};

export default React.memo(CustomerFilters);